/**
 * Activity Stream WebSocket Hook
 *
 * Manages a WebSocket connection to the activity stream endpoint with
 * auto-reconnect, pause/resume, and filter support.
 *
 * Phase 3.5-C: Operational Intelligence
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { ActivityItem, ActivityFilter, ConnectionStatus } from '../types/activity';

const WS_PATH = '/api/admin/dashboard/stream';
const RECONNECT_DELAY_MS = 3000;
const MAX_ITEMS = 100;
const PING_INTERVAL_MS = 30_000;

export interface UseActivityStreamOptions {
  filter?: Partial<ActivityFilter>;
  onActivity?: (item: ActivityItem) => void;
  autoConnect?: boolean;
}

export interface UseActivityStreamResult {
  items: ActivityItem[];
  connectionStatus: ConnectionStatus;
  isPaused: boolean;
  pause: () => void;
  resume: () => void;
  connect: () => void;
  disconnect: () => void;
  acknowledge: (activityId: string) => void;
  clearItems: () => void;
}

export function useActivityStream(
  options: UseActivityStreamOptions = {},
): UseActivityStreamResult {
  const { filter, onActivity, autoConnect = true } = options;

  const [items, setItems] = useState<ActivityItem[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [isPaused, setIsPaused] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const isPausedRef = useRef(isPaused);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const filterRef = useRef(filter);
  const onActivityRef = useRef(onActivity);
  const shouldReconnectRef = useRef(true);

  // Keep refs in sync
  isPausedRef.current = isPaused;
  filterRef.current = filter;
  onActivityRef.current = onActivity;

  const clearTimers = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
  }, []);

  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false;
    clearTimers();
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnectionStatus('disconnected');
  }, [clearTimers]);

  const connect = useCallback(() => {
    // Clean up any existing connection
    clearTimers();
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    shouldReconnectRef.current = true;
    setConnectionStatus('connecting');

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${protocol}//${window.location.host}${WS_PATH}`);

    ws.onopen = () => {
      setConnectionStatus('connected');

      // Subscribe with filter
      ws.send(
        JSON.stringify({
          type: 'subscribe',
          channel: 'activities',
          filter: filterRef.current ?? null,
        }),
      );

      // Start ping interval
      pingTimerRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, PING_INTERVAL_MS);
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.type === 'activity' && !isPausedRef.current) {
          const item: ActivityItem = message.item;
          setItems((prev) => [item, ...prev].slice(0, MAX_ITEMS));
          onActivityRef.current?.(item);
        }
      } catch {
        // Ignore malformed messages
      }
    };

    ws.onclose = () => {
      setConnectionStatus('disconnected');
      clearTimers();

      if (shouldReconnectRef.current) {
        reconnectTimerRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
      }
    };

    ws.onerror = () => {
      // onclose will fire after onerror, triggering reconnect
    };

    wsRef.current = ws;
  }, [clearTimers]);

  const pause = useCallback(() => setIsPaused(true), []);
  const resume = useCallback(() => setIsPaused(false), []);

  const acknowledge = useCallback((activityId: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({ type: 'acknowledge', activity_id: activityId }),
      );
      setItems((prev) =>
        prev.map((item) =>
          item.id === activityId ? { ...item, is_acknowledged: true } : item,
        ),
      );
    }
  }, []);

  const clearItems = useCallback(() => setItems([]), []);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      connect();
    }
    return () => {
      shouldReconnectRef.current = false;
      clearTimers();
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [autoConnect, connect, clearTimers]);

  // Re-subscribe when filter changes
  useEffect(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN && filter) {
      wsRef.current.send(
        JSON.stringify({
          type: 'subscribe',
          channel: 'activities',
          filter,
        }),
      );
    }
  }, [filter]);

  return {
    items,
    connectionStatus,
    isPaused,
    pause,
    resume,
    connect,
    disconnect,
    acknowledge,
    clearItems,
  };
}

export default useActivityStream;
